'use client';

import { usePathname } from 'next/navigation';
import PageLayout from '../PageLayout';
import { LangLink } from '../../i18n/links';
import { defaultLang, SUPPORTED } from '../../config/languages';

const copy = {
  en: {
    title: 'Page not found',
    description: 'The page you are looking for does not exist or has been moved.',
    home: 'Go to homepage',
    solutions: 'Browse solutions',
    support: 'Contact support',
  },
  de: {
    title: 'Seite nicht gefunden',
    description: 'Die gesuchte Seite existiert nicht oder wurde verschoben.',
    home: 'Zur Startseite',
    solutions: 'Lösungen ansehen',
    support: 'Support kontaktieren',
  },
  es: {
    title: 'Página no encontrada',
    description: 'La página que buscas no existe o se ha movido.',
    home: 'Ir al inicio',
    solutions: 'Ver soluciones',
    support: 'Contactar soporte',
  },
  fr: {
    title: 'Page introuvable',
    description: 'La page que vous recherchez n’existe pas ou a été déplacée.',
    home: "Aller à l'accueil",
    solutions: 'Voir les solutions',
    support: 'Contacter le support',
  },
  it: {
    title: 'Pagina non trovata',
    description: 'La pagina che stai cercando non esiste o è stata spostata.',
    home: 'Vai alla homepage',
    solutions: 'Vedi le soluzioni',
    support: 'Contatta il supporto',
  },
  pt: {
    title: 'Página não encontrada',
    description: 'A página que você procura não existe ou foi movida.',
    home: 'Ir para a página inicial',
    solutions: 'Ver soluções',
    support: 'Falar com o suporte',
  },
  sr: {
    title: 'Stranica nije pronađena',
    description: 'Stranica koju tražiš ne postoji ili je premeštena.',
    home: 'Idi na početnu',
    solutions: 'Pogledaj rešenja',
    support: 'Kontaktiraj podršku',
  },
  pl: {
    title: 'Nie znaleziono strony',
    description: 'Strona, której szukasz, nie istnieje lub została przeniesiona.',
    home: 'Przejdź do strony głównej',
    solutions: 'Zobacz rozwiązania',
    support: 'Skontaktuj się z pomocą',
  },
  nl: {
    title: 'Pagina niet gevonden',
    description: 'De pagina die je zoekt bestaat niet meer of is verplaatst.',
    home: 'Naar de homepage',
    solutions: 'Bekijk oplossingen',
    support: 'Contact opnemen met support',
  },
  ja: {
    title: 'ページが見つかりません',
    description: 'お探しのページは存在しないか、移動した可能性があります。',
    home: 'ホームへ戻る',
    solutions: 'ソリューションを見る',
    support: 'サポートに問い合わせる',
  },
};

const getLangFromPath = (pathname = '') => {
  const segment = pathname.split('/').filter(Boolean)[0];
  return SUPPORTED.includes(segment) ? segment : defaultLang;
};

export default function NotFoundPage() {
  const pathname = usePathname();
  const lang = getLangFromPath(pathname);
  const text = copy[lang] || copy.en;
  const canonicalPath = lang === defaultLang ? '/404' : `/${lang}/404`;

  return (
    <PageLayout
      presentLikeHome={true}
      title={text.title}
      metaDescription={text.description}
      canonical={`https://www.adcast.app${canonicalPath}`}
      noindex
    >
      <link rel="stylesheet" href="/assets/css/not-found-layout-2.css" />
      <main className="not-found-v2">
        <div className="container not-found-v2__container">
          <section className="not-found-v2__panel" aria-labelledby="not-found-title">
            <div className="not-found-v2__orb not-found-v2__orb--blue" aria-hidden="true" />
            <div className="not-found-v2__orb not-found-v2__orb--orange" aria-hidden="true" />
            <p className="not-found-v2__code" aria-hidden="true">404</p>
            <div className="not-found-v2__copy">
              <span>AdCast</span>
              <h1 id="not-found-title">{text.title}</h1>
              <p>{text.description}</p>
              <nav className="not-found-v2__actions" aria-label="Next steps">
                <LangLink to="/" className="not-found-v2__primary">{text.home} <span aria-hidden="true">→</span></LangLink>
                <LangLink to="/solutions" className="not-found-v2__secondary">{text.solutions}</LangLink>
                <LangLink to="/support" className="not-found-v2__text-link">{text.support}</LangLink>
              </nav>
            </div>
          </section>
        </div>
      </main>
    </PageLayout>
  );
}
